(function () {
  if (window.__freeGiftOfferLoaded) return;
  window.__freeGiftOfferLoaded = true;

  const configElement = document.getElementById('free-gift-offer-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (_) {
    return;
  }
  if (!config.campaignId || !config.variantId) return;

  const rootPath = window.Shopify?.routes?.root || '/';
  const COOKIE_NAME = 'caffeine_gift';
  // Line property the gift discount Function looks for. Underscore keys stay
  // hidden in the cart and at checkout.
  const GIFT_PROPERTY = '_caffeine_gift';
  // Cart attribute carrying the visitor's campaign to the Function, which
  // cannot read cookies.
  const CART_ATTRIBUTE = '_caffeine_gift_campaign';

  const qualifyingValues = String(config.values || '')
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);

  function offerActive() {
    return !config.endsAt || Date.now() < config.endsAt;
  }

  function readCookie() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(value) {
    let maxAge = Number(config.cookieDays || 30) * 86400;
    if (config.endsAt) maxAge = Math.min(maxAge, Math.floor((config.endsAt - Date.now()) / 1000));
    if (maxAge <= 0) return;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
  }

  function captureFromUrl() {
    if (!offerActive() || qualifyingValues.length === 0) return;
    // Several parameter names may qualify (e.g. "offer, utm_campaign"): Klaviyo
    // overwrites utm_campaign on email links, so email uses its own parameter.
    const params = new URLSearchParams(window.location.search);
    const names = String(config.parameter || 'utm_campaign')
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean);
    const qualifies = names.some((name) => {
      const value = params.get(name);
      return value && qualifyingValues.includes(value.trim().toLowerCase());
    });
    if (qualifies) writeCookie(config.campaignId);
  }

  // Offer analytics for the Caffeine Commerce portal (Free gift offer report):
  // one event per type per browser session. Respects Shopify's customer
  // privacy API when it is loaded; fire-and-forget, never blocks the cart.
  const EVENTS_URL = 'https://admin.caffeinecommerce.com/api/events';
  const ANALYTICS_FEATURE = 'tera-free-gift';

  function analyticsAllowed() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') return privacy.analyticsProcessingAllowed();
    } catch (_) {
      return false;
    }
    return true;
  }

  function analyticsSessionId() {
    try {
      let sid = sessionStorage.getItem('caffeine_gift_sid');
      if (!sid) {
        sid = `fg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        sessionStorage.setItem('caffeine_gift_sid', sid);
      }
      return sid;
    } catch (_) {
      return null;
    }
  }

  function trackOnce(eventType, placement) {
    const shop = window.Shopify?.shop;
    const sessionId = analyticsSessionId();
    if (!shop || !sessionId || !analyticsAllowed()) return;
    const sentKey = `caffeine_gift_sent:${eventType}`;
    try {
      if (sessionStorage.getItem(sentKey)) return;
      sessionStorage.setItem(sentKey, '1');
    } catch (_) {
      return;
    }
    try {
      fetch(EVENTS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        // item = the visitor's market country, so the report compares orders
        // from the same countries the offer runs in.
        body: JSON.stringify({
          shop,
          feature: ANALYTICS_FEATURE,
          eventType,
          sessionId,
          placement,
          item: /^[A-Z]{2}$/.test(window.Shopify?.country || '') ? window.Shopify.country : '',
        }),
      }).catch(() => {});
    } catch (_) {
      // Analytics must never interrupt the storefront.
    }
  }

  function trackOfferViews() {
    if (!isEligible()) return;
    const shown = Array.from(document.querySelectorAll('[data-free-gift-callout]'))
      .some((el) => el.offsetParent !== null && !el.closest('#cart-drawer'));
    if (shown) trackOnce('impression', 'pdp');
  }

  function isEligible() {
    return offerActive() && readCookie() === config.campaignId;
  }

  function isGiftLine(item) {
    return Boolean(item.properties?.[GIFT_PROPERTY]);
  }

  function hasQualifyingLine(cart) {
    return (cart.items || []).some((item) => item.selling_plan_allocation && !isGiftLine(item));
  }

  function bundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections },
    }));
    return sections;
  }

  async function postCart(path, body) {
    const response = await fetch(`${rootPath}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || 'The cart could not be updated.');
    return json;
  }

  // Set once a gift add fails (e.g. sold out) so a broken add can't loop.
  let addFailed = false;

  // Brings the cart in line with the offer: one gift line while the visitor is
  // eligible and the cart holds a subscription, none otherwise. Returns true
  // when it changed the cart's lines.
  async function syncCart(cart) {
    const eligible = isEligible();
    const items = cart.items || [];
    const giftLines = items.filter(isGiftLine);
    const wantGift = eligible && hasQualifyingLine(cart);

    const keptGift = wantGift
      ? giftLines.find((item) => item.properties[GIFT_PROPERTY] === config.campaignId && item.variant_id === config.variantId)
      : undefined;

    const updates = {};
    giftLines.forEach((item) => {
      if (item !== keptGift) updates[item.key] = 0;
      else if (item.quantity !== 1) updates[item.key] = 1;
    });

    const attributes = {};
    const currentAttribute = cart.attributes?.[CART_ATTRIBUTE] || '';
    if (eligible && items.length > 0 && currentAttribute !== config.campaignId) attributes[CART_ATTRIBUTE] = config.campaignId;
    if (!eligible && currentAttribute) attributes[CART_ATTRIBUTE] = '';

    const needsAdd = wantGift && !keptGift && !addFailed;
    const changesLines = Object.keys(updates).length > 0 || needsAdd;
    if (!changesLines && Object.keys(attributes).length === 0) return false;

    const sections = changesLines ? bundledSections() : [];
    let sectionsResponse = {};

    if (Object.keys(updates).length > 0 || Object.keys(attributes).length > 0) {
      const updated = await postCart('cart/update.js', { updates, attributes, sections, sections_url: window.location.pathname });
      sectionsResponse = updated.sections || sectionsResponse;
    }

    if (needsAdd) {
      try {
        const added = await postCart('cart/add.js', {
          items: [{ id: config.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: config.campaignId } }],
          sections,
          sections_url: window.location.pathname,
        });
        sectionsResponse = added.sections || sectionsResponse;
        window.dataLayer?.push({ event: 'caffeine_free_gift_added', campaign_id: config.campaignId, variant_id: config.variantId });
        trackOnce('gift_added', 'cart_drawer');
      } catch (_) {
        addFailed = true;
      }
    }

    if (!changesLines) return false;

    const updatedCart = await fetch(`${rootPath}cart.js`, { credentials: 'same-origin' }).then((result) => result.json());
    updatedCart.sections = sectionsResponse;
    // baseEvent 'variant:add' makes the drawer swap its lines immediately
    // instead of after its 1.25s line-removal animation.
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', cart: updatedCart, freeGiftSync: true },
    }));
    return true;
  }

  // Serialize syncs: cart events can arrive back to back.
  let queue = Promise.resolve();
  function scheduleSync(getCart) {
    queue = queue
      .then(async () => syncCart(await getCart()))
      .catch(() => false);
    return queue;
  }

  const fetchCart = () => fetch(`${rootPath}cart.js`, { credentials: 'same-origin' }).then((result) => result.json());

  captureFromUrl();
  document.documentElement.classList.toggle('free-gift-eligible', isEligible());

  // One visit per browser session: campaign if the visitor is cookied from the
  // campaign link, otherwise baseline (the comparison group). A session that
  // becomes eligible later in the visit also counts as a campaign visit.
  if (offerActive()) {
    let campaignSent = false;
    try { campaignSent = Boolean(sessionStorage.getItem('caffeine_gift_sent:campaign_visit')); } catch (_) { /* no storage */ }
    if (isEligible()) trackOnce('campaign_visit', 'storefront');
    else if (!campaignSent) trackOnce('baseline_visit', 'storefront');
  }
  if (document.readyState === 'complete') trackOfferViews();
  else window.addEventListener('load', trackOfferViews, { once: true });

  // Run for every visitor, not only eligible ones, so a gift line is removed
  // once the cookie expires or the offer ends.
  scheduleSync(fetchCart).then((changed) => {
    // The cart page changes lines with full page loads; reload to show the result.
    if (changed && window.themeVariables?.settings?.pageType === 'cart') window.location.reload();
  });

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.freeGiftSync) return;
    const cart = event.detail?.cart;
    scheduleSync(() => (cart?.items ? cart : fetchCart()));
  });

  // The line-item plan selector (subscription <-> one-time) only fires cart:refresh.
  document.addEventListener('cart:refresh', () => scheduleSync(fetchCart));

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleSync(fetchCart);
  });
})();
